import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Skeleton,
  Chip,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  SmartToy as SmartToyIcon,
  TheaterComedy as TheaterComedyIcon,
  RocketLaunch as RocketLaunchIcon,
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
  Forum as ForumIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';
import { PAAQuestionSelector } from '../../components/generative-search/PAAQuestionSelector';
import { QueryExpander } from '../../components/generative-search/QueryExpander';

interface ModelResponse {
  model: string;
  modelName: string;
  response: string | null;
  success: boolean;
  error?: string;
  sources?: any[];
}

interface AIResponse {
  message: string;
  responses: ModelResponse[];
}

const MODEL_COLORS = {
  gemini: '#4285f4',
  gpt4: '#00a67e',
  claude: '#6b46c1',
  grok: '#000000',
  perplexity: '#20b2aa',
};

const MODEL_ICONS = {
  gemini: AutoAwesomeIcon,
  gpt4: SmartToyIcon,
  claude: TheaterComedyIcon,
  grok: RocketLaunchIcon,
  perplexity: SearchIcon,
};

// Helper function to parse text with bold markers and citations
const parseTextWithFormatting = (text: string) => {
  const parts = [];
  let lastIndex = 0;
  
  // Parse bold text marked with **text** and remove the markers
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let match;
  
  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), bold: false });
    }
    // Add the bold text without the ** markers
    parts.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), bold: false });
  }
  
  // If no bold text found, return the original text
  if (parts.length === 0) {
    parts.push({ text, bold: false });
  }
  
  return parts;
};

// Helper function to extract citations from text
const extractCitations = (text: string) => {
  const citations = new Map<string, string>();
  const citationRegex = /\[(\d+)\]/g;
  const matches = text.match(citationRegex);
  
  if (matches) {
    matches.forEach(match => {
      const num = match.replace(/[\[\]]/g, '');
      if (!citations.has(num)) {
        citations.set(num, `Source ${num}`);
      }
    });
  }
  
  return citations;
};

// Helper function to render text with citations
const renderTextWithCitations = (text: string) => {
  const parts = [];
  let lastIndex = 0;
  const citationRegex = /\[(\d+)\]/g;
  let match;
  
  while ((match = citationRegex.exec(text)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      parts.push(
        ...parseTextWithFormatting(textBefore).map((part, idx) => ({
          ...part,
          type: 'text',
          key: `text-${lastIndex}-${idx}`
        }))
      );
    }
    
    // Add citation
    parts.push({
      type: 'citation',
      text: match[1],
      key: `citation-${match.index}`
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    parts.push(
      ...parseTextWithFormatting(remainingText).map((part, idx) => ({
        ...part,
        type: 'text',
        key: `text-${lastIndex}-${idx}`
      }))
    );
  }
  
  return parts;
};

// Helper function to format response text
const formatResponse = (text: string, modelName: string) => {
  // Extract citations first
  const citations = extractCitations(text);
  const hasCitations = citations.size > 0;
  
  // Split by double newlines to identify paragraphs
  const sections = text.split(/\n\n+/);
  const formattedSections: React.ReactNode[] = [];
  
  sections.forEach((section, sectionIndex) => {
    // Check if this section looks like a numbered list
    if (/^\d+\.\s/.test(section)) {
      const items = section.split(/\n(?=\d+\.)/);
      formattedSections.push(
        <Box key={sectionIndex} sx={{ mb: 2 }}>
          {items.map((item, itemIndex) => {
            const match = item.match(/^(\d+\.)\s*(.*)$/s);
            if (match) {
              const [, number, content] = match;
              const lines = content.trim().split('\n');
              const title = lines[0];
              const description = lines.slice(1).join('\n').trim();
              
              return (
                <Box key={itemIndex} sx={{ mb: 2 }}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="body2" component="span">
                      {number} 
                    </Typography>
                    {renderTextWithCitations(title).map((part) => {
                      if (part.type === 'citation') {
                        return (
                          <Tooltip key={part.key} title={`Source ${part.text}`}>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                color: 'primary.main',
                                cursor: 'help',
                                textDecoration: 'none',
                                verticalAlign: 'super',
                                fontSize: '0.8em',
                                mx: 0.2
                              }}
                            >
                              [{part.text}]
                            </Typography>
                          </Tooltip>
                        );
                      }
                      return (
                        <Typography 
                          key={part.key} 
                          variant="body2" 
                          component="span"
                          sx={{ fontWeight: 'bold' in part && part.bold ? 600 : 400 }}
                        >
                          {part.text}
                        </Typography>
                      );
                    })}
                  </Box>
                  {description && (
                    <Typography variant="body2" sx={{ pl: 3, color: 'text.secondary' }}>
                      {renderTextWithCitations(description).map((part) => {
                        if (part.type === 'citation') {
                          return (
                            <Tooltip key={part.key} title={`Source ${part.text}`}>
                              <Typography
                                component="span"
                                sx={{
                                  color: 'primary.main',
                                  cursor: 'help',
                                  verticalAlign: 'super',
                                  fontSize: '0.8em',
                                  mx: 0.2
                                }}
                              >
                                [{part.text}]
                              </Typography>
                            </Tooltip>
                          );
                        }
                        return (
                          <Typography
                            key={part.key}
                            component="span"
                            sx={{ fontWeight: 'bold' in part && part.bold ? 600 : 400 }}
                          >
                            {part.text}
                          </Typography>
                        );
                      })}
                    </Typography>
                  )}
                </Box>
              );
            }
            return <Typography key={itemIndex} variant="body2" sx={{ mb: 1 }}>{item}</Typography>;
          })}
        </Box>
      );
      return;
    }
    
    // Check if this section has headers marked with # or contains lists
    if (section.match(/^#/m) || section.includes('*   ') || section.includes('-   ')) {
      const lines = section.split('\n');
      formattedSections.push(
        <Box key={sectionIndex} sx={{ mb: 2 }}>
          {lines.map((line, lineIndex) => {
            // Handle headers (both # and ##)
            if (line.startsWith('#')) {
              const headerText = line.replace(/^#+\s*/, '');
              return (
                <Typography key={lineIndex} variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                  {renderTextWithCitations(headerText).map((part) => {
                    if (part.type === 'citation') {
                      return (
                        <Tooltip key={part.key} title={`Source ${part.text}`}>
                          <Typography
                            component="span"
                            sx={{
                              color: 'primary.main',
                              cursor: 'help',
                              verticalAlign: 'super',
                              fontSize: '0.8em',
                              mx: 0.2
                            }}
                          >
                            [{part.text}]
                          </Typography>
                        </Tooltip>
                      );
                    }
                    return (
                      <Typography
                        key={part.key}
                        component="span"
                        sx={{ fontWeight: 600 }}
                      >
                        {part.text}
                      </Typography>
                    );
                  })}
                </Typography>
              );
            }
            
            // Handle bullet points (both - and *)
            const bulletMatch = line.match(/^(\s*)([-*])\s+(.*)$/);
            if (bulletMatch) {
              const [, indent, bullet, content] = bulletMatch;
              const indentLevel = Math.floor(indent.length / 4); // Assuming 4 spaces per indent level
              return (
                <Box key={lineIndex} sx={{ display: 'flex', mb: 0.5, ml: indentLevel * 3 }}>
                  <Typography variant="body2" sx={{ mr: 1, minWidth: '16px' }}>
                    {indentLevel === 0 ? '•' : '◦'}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {renderTextWithCitations(content).map((part) => {
                      if (part.type === 'citation') {
                        return (
                          <Tooltip key={part.key} title={`Source ${part.text}`}>
                            <Typography
                              component="span"
                              sx={{
                                color: 'primary.main',
                                cursor: 'help',
                                verticalAlign: 'super',
                                fontSize: '0.8em',
                                mx: 0.2
                              }}
                            >
                              [{part.text}]
                            </Typography>
                          </Tooltip>
                        );
                      }
                      return (
                        <Typography
                          key={part.key}
                          component="span"
                          sx={{ fontWeight: 'bold' in part && part.bold ? 600 : 400 }}
                        >
                          {part.text}
                        </Typography>
                      );
                    })}
                  </Typography>
                </Box>
              );
            }
            return line.trim() ? (
              <Typography key={lineIndex} variant="body2" sx={{ mb: 0.5 }}>
                {renderTextWithCitations(line).map((part) => {
                  if (part.type === 'citation') {
                    return (
                      <Tooltip key={part.key} title={`Source ${part.text}`}>
                        <Typography
                          component="span"
                          sx={{
                            color: 'primary.main',
                            cursor: 'help',
                            verticalAlign: 'super',
                            fontSize: '0.8em',
                            mx: 0.2
                          }}
                        >
                          [{part.text}]
                        </Typography>
                      </Tooltip>
                    );
                  }
                  return (
                    <Typography
                      key={part.key}
                      component="span"
                      sx={{ fontWeight: 'bold' in part && part.bold ? 600 : 400 }}
                    >
                      {part.text}
                    </Typography>
                  );
                })}
              </Typography>
            ) : null;
          })}
        </Box>
      );
      return;
    }
    
    // Check if this looks like a table
    if (section.includes('|') && section.split('\n').filter(line => line.includes('|')).length > 2) {
      const lines = section.split('\n').filter(line => line.trim());
      const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
      const rows = lines.slice(2).map(line => 
        line.split('|').map(cell => cell.trim()).filter(cell => cell)
      );
      
      formattedSections.push(
        <Box key={sectionIndex} sx={{ mb: 2, overflowX: 'auto' }}>
          <Box sx={{ minWidth: 'max-content', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
              {headers.map((header, idx) => (
                <Typography key={idx} variant="body2" sx={{ p: 1, fontWeight: 600, minWidth: 150, borderRight: idx < headers.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                  {parseTextWithFormatting(header).map((part, partIdx) => (
                    <Typography
                      key={partIdx}
                      component="span"
                      sx={{ fontWeight: 600 }}
                    >
                      {part.text}
                    </Typography>
                  ))}
                </Typography>
              ))}
            </Box>
            {rows.map((row, rowIdx) => (
              <Box key={rowIdx} sx={{ display: 'flex', borderBottom: rowIdx < rows.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                {row.map((cell, cellIdx) => (
                  <Typography key={cellIdx} variant="body2" sx={{ p: 1, minWidth: 150, borderRight: cellIdx < row.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                    {parseTextWithFormatting(cell).map((part, partIdx) => (
                      <Typography
                        key={partIdx}
                        component="span"
                        sx={{ fontWeight: part.bold ? 600 : 400 }}
                      >
                        {part.text}
                      </Typography>
                    ))}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      );
      return;
    }
    
    // Default paragraph formatting with bold support and citations
    formattedSections.push(
      <Typography key={sectionIndex} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        {renderTextWithCitations(section).map((part) => {
          if (part.type === 'citation') {
            return (
              <Tooltip key={part.key} title={`Source ${part.text}`}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: 'primary.main',
                    cursor: 'help',
                    textDecoration: 'none',
                    verticalAlign: 'super',
                    fontSize: '0.8em',
                    mx: 0.2
                  }}
                >
                  [{part.text}]
                </Typography>
              </Tooltip>
            );
          }
          return (
            <Typography
              key={part.key}
              variant="body2"
              component="span"
              sx={{ fontWeight: 'bold' in part && part.bold ? 600 : 400 }}
            >
              {part.text}
            </Typography>
          );
        })}
      </Typography>
    );
  });
  
  
  // Add citation sources section if model is Perplexity and has citations
  if (modelName.toLowerCase() === 'perplexity' && hasCitations) {
    formattedSections.push(
      <Box key="citations" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          Sources
          <Tooltip title="Perplexity provides citations but does not expose the actual source URLs through their API">
            <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Tooltip>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Array.from(citations.entries()).map(([num]) => (
            <Box key={num} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  minWidth: '24px',
                  fontWeight: 500
                }}
              >
                [{num}]
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1, fontStyle: 'italic' }}>
                Source {num} - Referenced in text above
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  
  return formattedSections;
};

export const GenerativeSearchPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<ModelResponse[]>([]);
  const [copiedModel, setCopiedModel] = useState<string | null>(null);
  const [followUpQuestions, setFollowUpQuestions] = useState<Record<string, string>>({});
  const [followUpLoading, setFollowUpLoading] = useState<Record<string, boolean>>({});

  const handleSubmit = async () => {
    if (!query.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    setResponses([]);

    try {
      const result = await backendApiService.post('/api/openrouter/chat-all', {
        message: query
      });

      if (result.success && result.data) {
        setResponses(result.data.responses);
      } else {
        setError('Failed to get AI responses');
      }
    } catch (err: any) {
      console.error('AI Error:', err);
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const copyToClipboard = async (text: string, modelId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedModel(modelId);
      setTimeout(() => setCopiedModel(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleFollowUp = async (modelId: string) => {
    const question = followUpQuestions[modelId];
    if (!question?.trim()) return;

    setFollowUpLoading({ ...followUpLoading, [modelId]: true });
    
    try {
      const result = await backendApiService.post('/api/openrouter/chat', {
        message: question,
        model: modelId
      });

      if (result.success && result.data) {
        // Update the specific model's response
        setResponses(prev => prev.map(resp => 
          resp.model === modelId 
            ? { ...resp, response: result.data.message }
            : resp
        ));
        // Clear the follow-up question
        setFollowUpQuestions(prev => ({ ...prev, [modelId]: '' }));
      }
    } catch (err: any) {
      console.error('Follow-up error:', err);
    } finally {
      setFollowUpLoading({ ...followUpLoading, [modelId]: false });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Generative Search
        </Typography>
        <Typography variant="body1" color="text.secondary">
          AI-powered search tools for SEO insights
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<ForumIcon />} 
            label="AI Chat" 
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab 
            icon={<PsychologyIcon />} 
            label="Query Expander" 
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <Box>

      {/* PAA Question Selector */}
      <PAAQuestionSelector 
        onQuestionSelect={async (question) => {
          setQuery(question);
          setError(null); // Clear any existing errors
          
          // Automatically submit the question to all AI models
          setLoading(true);
          setResponses([]);
          
          // Scroll to the input section immediately for visual feedback
          setTimeout(() => {
            const inputSection = document.getElementById('generative-search-input');
            if (inputSection) {
              inputSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          
          try {
            const result = await backendApiService.post('/api/openrouter/chat-all', {
              message: question
            });

            if (result.success && result.data) {
              setResponses(result.data.responses);
              
              // After responses load, scroll to show them
              setTimeout(() => {
                const responsesSection = document.getElementById('ai-responses-section');
                if (responsesSection) {
                  responsesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            } else {
              setError('Failed to get AI responses');
            }
          } catch (err: any) {
            console.error('Error submitting question:', err);
            setError(err.message || 'Failed to get AI responses');
          } finally {
            setLoading(false);
          }
        }}
      />

      {/* Input Section */}
      <Paper id="generative-search-input" sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Ask a question"
            placeholder="What are the top tourist attractions in New York?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !query.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Loading...' : 'Ask'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Responses Grid */}
      {(loading || responses.length > 0) && (
        <Grid id="ai-responses-section" container spacing={3}>
          {loading ? (
            // Show skeletons while loading
            ['Gemini', 'Gpt4', 'Claude', 'Grok', 'Perplexity'].map((model) => (
              <Grid size={{ xs: 12, md: 6 }} key={model}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {model}
                      </Typography>
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                    <Skeleton variant="text" sx={{ mb: 1 }} />
                    <Skeleton variant="text" sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            // Show actual responses
            responses.map((response) => (
              <Grid size={{ xs: 12, md: 6 }} key={response.model}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderTop: `4px solid ${MODEL_COLORS[response.model as keyof typeof MODEL_COLORS] || '#ccc'}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(() => {
                          const IconComponent = MODEL_ICONS[response.model as keyof typeof MODEL_ICONS] || SmartToyIcon;
                          return <IconComponent sx={{ fontSize: 28, color: MODEL_COLORS[response.model as keyof typeof MODEL_COLORS] || '#666' }} />;
                        })()}
                        <Typography variant="h6" fontWeight={600}>
                          {response.modelName}
                        </Typography>
                      </Box>
                      {response.success && response.response && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {copiedModel === response.model && (
                            <Chip 
                              icon={<CheckCircleIcon />} 
                              label="Copied!" 
                              size="small" 
                              color="success" 
                              variant="outlined"
                            />
                          )}
                          <IconButton
                            onClick={() => copyToClipboard(response.response!, response.model)}
                            size="small"
                            sx={{ 
                              bgcolor: 'grey.100',
                              '&:hover': { bgcolor: 'grey.200' }
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {response.success ? (
                      <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                        {formatResponse(response.response || '', response.modelName)}
                      </Box>
                    ) : (
                      <Alert severity="error" sx={{ py: 1 }}>
                        {response.error || 'Failed to get response'}
                      </Alert>
                    )}
                    
                    {/* Follow-up Question Section */}
                    {response.success && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Ask a follow-up question:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Type your follow-up question..."
                            value={followUpQuestions[response.model] || ''}
                            onChange={(e) => setFollowUpQuestions(prev => ({
                              ...prev,
                              [response.model]: e.target.value
                            }))}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleFollowUp(response.model);
                              }
                            }}
                            disabled={followUpLoading[response.model]}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleFollowUp(response.model)}
                            disabled={!followUpQuestions[response.model]?.trim() || followUpLoading[response.model]}
                            startIcon={followUpLoading[response.model] ? <CircularProgress size={16} /> : <SendIcon />}
                          >
                            Ask
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}
        </Box>
      ) : (
        <QueryExpander />
      )}
    </Box>
  );
};